import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdChevronRight } from 'react-icons/md';
import fetchStoreDetails, { StoreLocation, fetchNearbyStores, NearbyStore } from '../../services/storeMapService';
import StoreFilters, { FilterState } from './StoreFilters';
import StoreSidebar from './StoreSidebar';
import MapHeader from './MapHeader';
import { GoogleMapView, GoogleMapViewHandle, MapStore } from '../../components/GoogleMapView';
import { exportStoreMapData, RecommendationConfigs } from '../../utils/excelExportUtils';
import { fetchAllChannels } from '../../services/mapPageService';
import { normalizeChannel } from '../../utils/channelNormalizer';
import { useConfigurationStore, useExportStore, selectStoresForBrand, SolutionConfig } from '../../stores';
import { calculateStoreRecommendations } from '../../utils/recommendationLogic';
import StreetViewPanel from '../opportunityByRegion/StreetViewPanel';
import HeatmapToggle from '../../components/HeatmapToggle/HeatmapToggle';
import './StoresMapPage.css';

declare global {
  interface Window {
    google: typeof google;
  }
}

// Helper to build active conditions from a solution config
const buildActiveConditions = (config: SolutionConfig) => {
  const activeId = config.activeReadiness[0];
  const activeLevel = config.readinessLevels.find(l => l.id === activeId);
  const conditions = activeLevel ? config.conditions.reduce((acc, cond) => {
    acc[cond.metric] = (cond as unknown as Record<string, string>)[activeId];
    return acc;
  }, {} as Record<string, string>) : {};
  return { conditions, readinessName: activeLevel?.name };
};

// Helper to check if store has a solution
const hasSolution = (store: NearbyStore, code: string): boolean => {
  switch (code) {
    case 'P': return !!store.purchek_install_date;
    case 'CM': return !!store.cart_manager_install_date;
    case 'CC': return !!store.containment_install_date;
    case 'S': return !!store.subscription_start_date;
    case 'PW': return !!store.paired_wheel_install_date;
    default: return false;
  }
};

// Convert NearbyStore to MapStore for GoogleMapView
const toMapStore = (store: NearbyStore): MapStore => ({
  id: store.id!,
  brand_name: store.brand_name ?? undefined,
  site: store.site ?? undefined,
  latitude: store.latitude!,
  longitude: store.longitude!,
  customer_id: store.customer_id ?? undefined,
  channel: store.channel ?? undefined,
  address: store.address ?? undefined,
  city: store.city ?? undefined,
  state: store.state ?? undefined,
  zipcode: store.zipcode ?? undefined,
  purchek_install_date: store.purchek_install_date ?? undefined,
  containment_install_date: store.containment_install_date ?? undefined,
  cart_manager_install_date: store.cart_manager_install_date ?? undefined,
  subscription_start_date: store.subscription_start_date ?? undefined,
  paired_wheel_install_date: store.paired_wheel_install_date ?? undefined,
  estimated_no_of_carts_per_store: store.estimated_no_of_carts_per_store ?? undefined,
  geo: {
    city: store.city ?? undefined,
    state_code: store.state ?? undefined,
    total_crime_incidents: store.crime_rate ?? 0,
  },
  nearby_penetration: {
    plaza_cm_customer_count: store.plaza_cm_customer_count ?? 0,
  },
  recommendations: (store as any).recommendations,
  distance: store.distance,
  total_crime_amount_usd: store.total_crime_amount_usd,
  video_incident_count: store.video_incident_count,
  is_customer: store.is_customer,
  store_number: (store as any).store_number ?? null,
  updated_store_ids: (store as any).updated_store_ids ?? null,
});

export default function StoresMapPage() {
  const navigate = useNavigate();
  const { storeid: poiId } = useParams<{ storeid?: string }>();
  const { purchek, cartManager, containment, subscription, pairedWheel } = useConfigurationStore();
  const mapViewRef = useRef<GoogleMapViewHandle>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [storeDetails, setStoreDetails] = useState<StoreLocation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nearbyStores, setNearbyStores] = useState<NearbyStore[]>([]);
  const [refRecommendations, setRefRecommendations] = useState<any>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [streetViewStore, setStreetViewStore] = useState<{ lat: number; lng: number; name?: string } | null>(null);
  const [availableChannels, setAvailableChannels] = useState<string[]>([]);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    storeType: 'All Stores',
    radius: 3.1,
    channels: [],
    mapType: 'Road',
    showOption: 'All Stores in Neighbourhood',
    storesHave: [],
    storesDontHave: [],
    showEntireNeighborhood: false,
    limitGeofence: false
  });

  // Zustand export store — keyed by brand name
  const brandKey = storeDetails?.brand_name?.toUpperCase() || '';
  const toggleStoreExport = useExportStore((s) => s.toggleStore);
  const selectedExportIds = useExportStore(selectStoresForBrand(brandKey));

  // Fetch available channels
  useEffect(() => {
    fetchAllChannels().then(setAvailableChannels);
  }, []);

  // Hold the latest store details in a ref so nearby-store refetches can access it
  // without needing to re-fetch store details again
  const storeDetailsRef = useRef<StoreLocation | null>(null);

  // Shared helper to load nearby stores (and recommendations) given a StoreLocation
  const loadNearbyStores = useCallback(async (
    details: StoreLocation,
    radius: number,
    cancelled: () => boolean,
  ) => {
    if (!details.latitude || !details.longitude || !details.brand_name) {
      setNearbyStores([]);
      setLoading(false);
      return;
    }

    try {
      const nearby = await fetchNearbyStores(
        details.latitude!,
        details.longitude!,
        radius,
        poiId
      );
      if (cancelled()) return;

      const purchekCfg = buildActiveConditions(purchek);
      const cartManagerCfg = buildActiveConditions(cartManager);
      const containmentCfg = buildActiveConditions(containment);
      const subscriptionCfg = buildActiveConditions(subscription);
      const pairedWheelCfg = buildActiveConditions(pairedWheel);

      const storesWithRecs = nearby.map((store) => {
        const storeForRecs = {
          ...store,
          nearby_penetration: {
            purchek_customer_count: store.purchek_customer_count ?? 0,
            penetration_purchek: store.penetration_purchek ?? 0,
            containment_customer_count: store.containment_customer_count ?? 0,
            penetration_containment: store.penetration_containment ?? 0,
            plaza_cm_customer_count: store.plaza_cm_customer_count ?? 0,
            penetration_paired_wheel: store.penetration_paired_wheel ?? 0,
          },
          geo: {
            total_crime_incidents: store.crime_rate ?? 0,
            no_of_hot_days: store.no_of_hot_days ?? 0,
            no_of_cold_days: store.no_of_cold_days ?? 0,
            penalty_for_cart_owner: store.penalty_for_cart_owner ?? null,
            law_summary: store.law_summary ?? null,
          },
        };
        return {
          ...store,
          recommendations: calculateStoreRecommendations(
            storeForRecs,
            store.estimated_no_of_carts_per_store || 0,
            purchekCfg.conditions,
            cartManagerCfg.conditions,
            containmentCfg.conditions,
            subscriptionCfg.conditions,
            pairedWheelCfg.conditions,
            purchekCfg.readinessName,
            cartManagerCfg.readinessName,
            containmentCfg.readinessName,
            subscriptionCfg.readinessName,
            pairedWheelCfg.readinessName
          ),
        };
      });

      const refForRecs = {
        ...details,
        nearby_penetration: {
          purchek_customer_count: details.purchek_customer_count ?? 0,
          penetration_purchek: details.penetration_purchek ?? 0,
          containment_customer_count: details.containment_customer_count ?? 0,
          penetration_containment: details.penetration_containment ?? 0,
          plaza_cm_customer_count: details.plaza_cm_customer_count ?? 0,
          penetration_paired_wheel: details.penetration_paired_wheel ?? 0,
        },
        geo: {
          total_crime_incidents: details.crime_rate ?? 0,
          no_of_hot_days: details.no_of_hot_days ?? 0,
          no_of_cold_days: details.no_of_cold_days ?? 0,
          penalty_for_cart_owner: details.penalty_for_cart_owner ?? null,
          law_summary: details.law_summary ?? null,
        },
      };
      const refRecs = calculateStoreRecommendations(
        refForRecs,
        details.estimated_no_of_carts_per_store || 0,
        purchekCfg.conditions,
        cartManagerCfg.conditions,
        containmentCfg.conditions,
        subscriptionCfg.conditions,
        pairedWheelCfg.conditions,
        purchekCfg.readinessName,
        cartManagerCfg.readinessName,
        containmentCfg.readinessName,
        subscriptionCfg.readinessName,
        pairedWheelCfg.readinessName
      );

      setRefRecommendations(refRecs);
      setNearbyStores(storesWithRecs);
    } catch {
      if (!cancelled()) setNearbyStores([]);
    } finally {
      if (!cancelled()) setLoading(false); // clears overlay for radius/filter refetches
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiId, purchek, cartManager, containment, subscription, pairedWheel]);

  // Fetch store details then immediately fetch nearby stores (no intermediate render)
  useEffect(() => {
    if (!poiId) {
      setError('No store ID provided');
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const run = async () => {
      setLoading(true);
      setStoreDetails(null);
      storeDetailsRef.current = null;
      setNearbyStores([]);
      setRefRecommendations(null);
      setError(null);

      try {
        const details = await fetchStoreDetails(poiId);
        if (isCancelled) return;

        if (!details) {
          setError('Store not found');
          setLoading(false);
          return;
        }

        // Set details in state AND ref simultaneously so no intermediate render shows
        storeDetailsRef.current = details;
        setStoreDetails(details);

        await loadNearbyStores(details, filters.radius, () => isCancelled);
      } catch {
        if (!isCancelled) {
          setError('Failed to load store data');
          setLoading(false);
        }
      }
    };

    run();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiId]);

  // Re-fetch nearby stores when radius or recommendation configs change (without re-fetching store details)
  useEffect(() => {
    const details = storeDetailsRef.current;
    if (!details) return; // initial load not done yet; the poiId effect handles it

    let isCancelled = false;
    setLoading(true);
    loadNearbyStores(details, filters.radius, () => isCancelled);
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.radius, purchek, cartManager, containment, subscription, pairedWheel]);

  // Enriched reference store with recommendations attached
  const enrichedStoreDetails = useMemo(() => {
    if (!storeDetails) return null;
    if (!refRecommendations) return storeDetails;
    return { ...storeDetails, recommendations: refRecommendations } as any;
  }, [storeDetails, refRecommendations]);

  // Handle map type changes via GoogleMapView ref
  useEffect(() => {
    const map = googleMapRef.current;
    if (!map) return;

    const mapTypeId =
      filters.mapType === 'Satellite'
        ? 'satellite'
        : filters.mapType === 'Hybrid'
        ? 'hybrid'
        : 'roadmap';

    map.setMapTypeId(mapTypeId);
  }, [filters.mapType]);

  // Handle radius-based zoom
  useEffect(() => {
    const map = googleMapRef.current;
    if (!map) return;

    let zoomLevel = 14;
    if (filters.radius > 4) zoomLevel = 12;
    else if (filters.radius > 3) zoomLevel = 13;
    else if (filters.radius > 2) zoomLevel = 13;
    else if (filters.radius > 1) zoomLevel = 14;
    else zoomLevel = 15;

    map.setZoom(zoomLevel);
  }, [filters.radius]);

  // Filter nearby stores based on current filters
  const filteredStores = useMemo(() => {
    return nearbyStores.filter((store) => {
      if (poiId && store.id === poiId) return false;

      const withinRadius = (store.distance || 0) <= filters.radius;

      const isCustomer = !!(store.purchek_install_date || store.containment_install_date || store.cart_manager_install_date || store.subscription_start_date || store.paired_wheel_install_date || store.customer_id);
      if (filters.storeType === 'Customers' && !isCustomer) return false;
      if (filters.storeType === 'Non-Customer' && isCustomer) return false;

      if (filters.storesHave.length > 0 && !filters.storesHave.every((s) => hasSolution(store, s))) return false;
      if (filters.storesDontHave.length > 0 && filters.storesDontHave.some((s) => hasSolution(store, s))) return false;

      if (filters.showOption === 'Located in Shopping Complex' && (!store.plaza_cm_customer_count || store.plaza_cm_customer_count <= 0)) return false;

      if (filters.channels.length > 0 && !filters.channels.includes(normalizeChannel(store.channel || ''))) return false;

      return withinRadius;
    });
  }, [nearbyStores, filters, poiId]);

  // Convert filtered stores + reference store to MapStore[] for GoogleMapView
  const mapStores = useMemo(() => {
    const stores: MapStore[] = [];

    // Add reference store first
    if (enrichedStoreDetails?.latitude && enrichedStoreDetails?.longitude && poiId) {
      stores.push(toMapStore({ ...enrichedStoreDetails, id: poiId } as NearbyStore));
    }

    // Add filtered nearby stores
    filteredStores.forEach((store) => {
      if (store.latitude && store.longitude && store.id) {
        stores.push(toMapStore(store));
      }
    });

    return stores;
  }, [enrichedStoreDetails, filteredStores, poiId]);

  // Export selection config for GoogleMapView
  const exportSelection = useMemo(() => {
    if (!brandKey) return undefined;
    return {
      selectedIds: selectedExportIds,
      onToggle: (storeId: string) => toggleStoreExport(brandKey, storeId),
    };
  }, [brandKey, selectedExportIds, toggleStoreExport]);

  // Callbacks
  const handleMapReady = useCallback((map: google.maps.Map) => {
    googleMapRef.current = map;
  }, []);

  const handleStreetViewClick = useCallback((position: { lat: number; lng: number }, name?: string) => {
    setStreetViewStore({ lat: position.lat, lng: position.lng, name });
  }, []);

  const handleMarkerClick = useCallback((store: MapStore) => {
    setSelectedStoreId(store.id);
  }, []);

  const handleOpenInfoWindow = useCallback((storeId: string) => {
    mapViewRef.current?.openInfoWindow(storeId);
    mapViewRef.current?.panToStore(storeId, 15);
  }, []);

  const handleExport = () => {
    if (filteredStores.length > 0 && storeDetails?.brand_name) {
      const purchekActive = buildActiveConditions(purchek);
      const cartManagerActive = buildActiveConditions(cartManager);
      const containmentActive = buildActiveConditions(containment);
      const subscriptionActive = buildActiveConditions(subscription);
      const pairedWheelActive = buildActiveConditions(pairedWheel);

      const configs: RecommendationConfigs = {
        purchekConfig: purchekActive.conditions,
        cartManagerConfig: cartManagerActive.conditions,
        containmentConfig: containmentActive.conditions,
        subscriptionConfig: subscriptionActive.conditions,
        pairedWheelConfig: pairedWheelActive.conditions,
        purchekReadinessName: purchekActive.readinessName,
        cartManagerReadinessName: cartManagerActive.readinessName,
        containmentReadinessName: containmentActive.readinessName,
        subscriptionReadinessName: subscriptionActive.readinessName,
        pairedWheelReadinessName: pairedWheelActive.readinessName,
      };

      exportStoreMapData(storeDetails.brand_name, filteredStores, enrichedStoreDetails, filters.radius, configs);
    }
  };

  // Map center from reference store
  const mapCenter = useMemo(() => {
    if (storeDetails?.latitude && storeDetails?.longitude) {
      return { lat: storeDetails.latitude, lng: storeDetails.longitude };
    }
    return undefined;
  }, [storeDetails]);

  return (
    <div className="stores-map-page">
      <MapHeader storeDetails={storeDetails} onBack={() => navigate(-1)} onExport={handleExport} nearbyStoresCount={nearbyStores.length} />

      {!filtersCollapsed && (
        <StoreFilters
          filters={filters}
          onFiltersChange={setFilters}
          nearbyStoresCount={nearbyStores.length}
          nearbyStores={nearbyStores}
          referenceBrand={storeDetails?.brand_name || ''}
          onClose={() => setFiltersCollapsed(true)}
          availableChannels={availableChannels}
        />
      )}

      <div className="stores-content-layout">
        <aside className={`store-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <StoreSidebar
            stores={nearbyStores}
            referenceStore={enrichedStoreDetails || undefined}
            selectedStoreId={selectedStoreId}
            onSelectStore={setSelectedStoreId}
            onOpenInfoWindow={handleOpenInfoWindow}
            mapViewRef={mapViewRef}
            radiusFilter={filters.radius}
            storeTypeFilter={filters.storeType}
            storesHaveFilter={filters.storesHave}
            storesDontHaveFilter={filters.storesDontHave}
            showOptionFilter={filters.showOption}
            channelFilter={filters.channels}
          />
        </aside>

        <button
          className="store-sidebar-toggle-btn"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <MdChevronRight size={20} className={`toggle-icon ${sidebarCollapsed ? '' : 'expanded'}`} />
        </button>

        <div className="stores-content" style={{ position: 'relative' }}>
          <div className="map-area">
            <GoogleMapView
              ref={mapViewRef}
              stores={mapStores}
              center={mapCenter}
              zoom={13}
              showHeatmap={showHeatmap}
              showDetailedMarkers={true}
              zoomThreshold={0}
              referenceStoreId={poiId}
              exportSelection={exportSelection}
              onMapReady={handleMapReady}
              onMarkerClick={handleMarkerClick}
              onStreetViewClick={handleStreetViewClick}
              height="100%"
            />
            <div className="map-legend">
              <span className="map-legend-item"><span className="map-legend-dot blue"></span>Customer</span>
              <span className="map-legend-item"><span className="map-legend-dot red"></span>Non-Customer</span>
              <span className="map-legend-divider"></span>
              <span className="map-legend-item"><span className="map-legend-badge">P</span>Purchek</span>
              <span className="map-legend-item"><span className="map-legend-badge">CC</span>Cart Containment</span>
              <span className="map-legend-item"><span className="map-legend-badge">CM</span>Cart Manager</span>
              <span className="map-legend-item"><span className="map-legend-badge">S</span>Subscription</span>
            </div>

            {loading && (
              <div className="map-loading">
                <div className="spinner"></div>
                <p>Loading map...</p>
              </div>
            )}

            {error && (
              <div className="map-error">
                <p>{error}</p>
              </div>
            )}
            <HeatmapToggle showHeatmap={showHeatmap} onToggle={setShowHeatmap} />
            {filtersCollapsed && (
              <button className="filters-toggle-btn" onClick={() => setFiltersCollapsed(false)} title="Show filters">
                Filters
              </button>
            )}
          </div>
          {streetViewStore && (
            <StreetViewPanel
              lat={streetViewStore.lat}
              lng={streetViewStore.lng}
              storeName={streetViewStore.name}
              onClose={() => setStreetViewStore(null)}
            />
          )}
        </div>
      </div>

    </div>
  );
}
